import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment, { Moment } from "moment-timezone";
import { scaleTime } from "d3-scale";
import { useSearchParams } from "react-router-dom";

import { bucketEnd, bucketStart } from "utils/date";
import { formatTimeShort } from "utils/format";
import Mouse from "ui/Graph/Mouse";
import Axis from "ui/Graph/Axis";
import {
  ScreenLocation,
  InteractionPoint,
  formatTimeAdaptive,
} from "ui/Graph/util";
import { translateY } from "utils/svg";

const MARKER_HEIGHT = 16;
const AXIS_HEIGHT = 16;
const HEIGHT = MARKER_HEIGHT + AXIS_HEIGHT;

function JumpToTimeBar({
  from,
  to,
  bucketMinutes,
  focus,
  onFocusChange,
}: {
  from: Moment;
  to: Moment;
  /** Granularity of time buckets */
  bucketMinutes: number;
  /** Optional instant within the range. Logs are show up to this point.
Clicking a bucket focuses its end. */
  focus: Moment | null;
  onFocusChange: (focus: Moment | null) => void;
}) {
  const [ref, width] = useElementWidth();
  const [hoverBucketMs, setHoverBucketMs] = useState<number | null>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);

  const { firstBucket, lastBucket } = bucketBounds(from, to, bucketMinutes);
  const scale = scaleTime().domain([from, to]).range([0, width]);

  const clampBucket = (bucket: Moment): Moment => {
    if (bucket.isBefore(firstBucket)) {
      return firstBucket;
    }
    if (bucket.isAfter(lastBucket)) {
      return lastBucket;
    }
    return bucket;
  };

  const mapLocation = (loc: ScreenLocation): InteractionPoint => {
    const bucket = clampBucket(
      bucketStart(moment(scale.invert(loc.x)), bucketMinutes),
    );
    const bucketMs = bucket.valueOf();
    return { bottom: { domain: bucketMs, range: scale(bucketMs) }, nearby: [] };
  };

  const bucketFocus = (startMs: number): Moment =>
    moment.min(bucketEnd(moment(startMs), bucketMinutes), to);

  const handleClick = (point: InteractionPoint): void => {
    const clicked = bucketFocus(point.bottom.domain);
    // The URL keeps whole seconds, so compare those (`to` may have milliseconds)
    onFocusChange(focus && clicked.unix() === focus.unix() ? null : clicked);
  };

  // From the start of the bucket the instant falls in (or ends) up to the instant itself
  const markerXs = (upTo: Moment) => ({
    startX: Math.max(
      0,
      scale(
        bucketStart(
          upTo.clone().subtract(1, "millisecond"),
          bucketMinutes,
        ).valueOf(),
      ),
    ),
    endX: Math.min(width, scale(upTo.valueOf())),
  });

  const active = hoverBucketMs != null ? bucketFocus(hoverBucketMs) : focus;
  const { startX: activeStartX, endX: activeEndX } = active
    ? markerXs(active)
    : { startX: null, endX: null };
  const LABEL_GAP = 5;
  // Sit to the right of the marker's end, flipping to the left of its start
  // once there's not enough room.
  const flipLabel =
    activeEndX != null &&
    labelWidth > 0 &&
    activeEndX + LABEL_GAP + labelWidth > width;
  const hasLabel = active != null;
  useLayoutEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.getBoundingClientRect().width);
    }
  }, [hasLabel]);

  return (
    // match TimeBucketBar's padding
    <div className="px-[5px]">
      <div className="relative">
        {active != null && activeStartX != null && activeEndX != null && (
          <span
            ref={labelRef}
            className="absolute top-0 whitespace-nowrap text-xs leading-none text-[#666] tabular-nums pointer-events-none"
            style={
              flipLabel
                ? { right: width - activeStartX + LABEL_GAP }
                : { left: activeEndX + LABEL_GAP }
            }
          >
            {formatTimeShort(active)}
          </span>
        )}
        <svg ref={ref} width="100%" height={HEIGHT} overflow="visible">
          {width > 0 && (
            <>
              <Mouse
                width={width}
                height={HEIGHT}
                onClick={handleClick}
                onHover={(point) =>
                  setHoverBucketMs(point ? point.bottom.domain : null)
                }
                mapLocationToInteractionPoint={mapLocation}
              >
                {(hover) => {
                  const marked = hover
                    ? bucketFocus(hover.bottom.domain)
                    : focus;
                  return marked ? (
                    <BucketMarker {...markerXs(marked)} hovering={!!hover} />
                  ) : null;
                }}
              </Mouse>
              <g pointerEvents="none" transform={translateY(MARKER_HEIGHT)}>
                <Axis
                  placement="bottom"
                  scale={scale}
                  width={width}
                  height={AXIS_HEIGHT}
                  tickFormat={formatTimeAdaptive}
                />
              </g>
            </>
          )}
        </svg>
      </div>
    </div>
  );
}

function BucketMarker({
  startX,
  endX,
  hovering,
}: {
  startX: number;
  endX: number;
  hovering: boolean;
}) {
  return (
    <g pointerEvents="none">
      <rect
        x={startX}
        y={0}
        width={Math.max(0, endX - startX)}
        height={MARKER_HEIGHT}
        fill={hovering ? "#72e2f6" : "#aeaeae"}
      />
      {/* Emphasize the bucket's end: that's the instant "show logs up to". */}
      <line
        x1={endX}
        x2={endX}
        y1={0}
        y2={MARKER_HEIGHT}
        strokeWidth={2}
        stroke={hovering ? "#1282a6" : "#797979"}
      />
    </g>
  );
}

const FOCUS_PARAM = "f";

export function useLogFocus(
  from: Moment,
  to: Moment,
): { focus: Moment | null; setFocus: (focus: Moment | null) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(FOCUS_PARAM);

  const fromUnix = from.unix();
  const toUnix = to.unix();

  const focus = useMemo(() => {
    if (!raw) {
      return null;
    }
    const focusUnix = parseInt(raw, 10);
    // Outside the range, treat as no focus (end of range).
    if (
      Number.isNaN(focusUnix) ||
      focusUnix <= fromUnix ||
      focusUnix > toUnix
    ) {
      return null;
    }
    return moment.unix(focusUnix);
  }, [raw, fromUnix, toUnix]);

  // If `f` is present but out of the (possibly newly-changed) range, drop it so
  // the URL reflects the implicit "focus the end of the range".
  useEffect(() => {
    if (raw && focus === null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(FOCUS_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [raw, focus, setSearchParams]);

  const setFocus = useCallback(
    (newFocus: Moment | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (newFocus === null) {
          next.delete(FOCUS_PARAM);
        } else {
          next.set(FOCUS_PARAM, String(newFocus.unix()));
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return { focus, setFocus };
}

// The buckets that overlap [from, to]: the first is the bucket containing `from` (its start may
// precede `from`); the last is the bucket whose start is the last boundary strictly before `to`.
function bucketBounds(
  from: Moment,
  to: Moment,
  bucketMinutes: number,
): { firstBucket: Moment; lastBucket: Moment } {
  const firstBucket = bucketStart(from, bucketMinutes);
  const lastBucket = bucketStart(
    to.clone().subtract(1, "millisecond"),
    bucketMinutes,
  );
  return {
    firstBucket,
    lastBucket: lastBucket.isBefore(firstBucket) ? firstBucket : lastBucket,
  };
}

function useElementWidth(): [React.RefObject<SVGSVGElement>, number] {
  const ref = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export default JumpToTimeBar;
